/**
 * Authentication Context Provider
 *
 * Provides auth state, login/logout actions, and permission checking
 * via React Context. No Redux dependency.
 *
 * Generated by CodeGen OS v1.0.0
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { LoginCredentials, AuthState } from './types';
import {
  loginAction,
  logoutAction,
  refreshAction,
  restoreAuth,
  tokenStorage,
} from './authStore';

// ---------------------------------------------------------------------------
// Context Types
// ---------------------------------------------------------------------------

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    // Restore session from localStorage on mount
    const user = restoreAuth();
    return {
      user,
      isAuthenticated: !!user,
      loading: false,
      error: null,
    };
  });

  // Proactive token refresh: check every 60s if token expires within 5 min
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const interval = setInterval(async () => {
      if (tokenStorage.isExpiringSoon(300)) {
        try {
          const user = await refreshAction();
          setState((prev) => ({ ...prev, user }));
        } catch {
          // Refresh failed — force logout
          tokenStorage.clear();
          setState({ user: null, isAuthenticated: false, loading: false, error: null });
        }
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [state.isAuthenticated]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { user } = await loginAction(credentials);
      setState({ user, isAuthenticated: true, loading: false, error: null });
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Authentication failed';
      setState((prev) => ({ ...prev, loading: false, error: message }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true }));
    await logoutAction();
    setState({ user: null, isAuthenticated: false, loading: false, error: null });
  }, []);

  const hasPermission = useCallback(
    (permission: string) => state.user?.permissions.includes(permission) ?? false,
    [state.user],
  );

  const hasRole = useCallback(
    (role: string) => state.user?.role === role,
    [state.user],
  );

  const hasAnyPermission = useCallback(
    (permissions: string[]) =>
      permissions.some((p) => state.user?.permissions.includes(p) ?? false),
    [state.user],
  );

  const hasAllPermissions = useCallback(
    (permissions: string[]) =>
      permissions.every((p) => state.user?.permissions.includes(p) ?? false),
    [state.user],
  );

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    hasPermission,
    hasRole,
    hasAnyPermission,
    hasAllPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Access auth state and actions. Must be used within <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export default AuthProvider;
